<template>
  <div class="autocomplete-wrapper">
    <input v-bind="$attrs" ref="autocomplete" autocomplete="off" :value="query" type="text"
           :placeholder="inputPlaceholder"
           @keydown="onKeyDown" @blur="onBlur" @focus="onFocus" @input="onInput">
    <ul v-if="displayOptions" class="autocomplete-options">
      <!-- Q: Why are we watching both @mousedown and @click on this element? Isn't @click enough?
           A: In some cases, as soon as mousedown occurs, the input "blur" fires and removes the <li>
              before the mouseup/click fires. This used to cause bugs where the selected option
              would not be used. Watching @mousedown fixes this issue. We continue to watch @click
              as well just in case @mousedown is not fired in some touchscreen contexts.
              In testing, triggering selectedOption on both events did not cause problems. -->
      <li v-for="autocompleteOption in autocompleteOptions" :key="autocompleteOption.placeId"
          :class="['autocomplete-option', {selected: optionIsFocused(autocompleteOption)}]"
          @mousedown="selectedOption(autocompleteOption)" @click="selectedOption(autocompleteOption)">
        <span class="main-matching-term">{{ autocompleteOption.mainMatchingTerm }}</span>
        <span class="secondary-matching-terms">{{ autocompleteOption.secondaryMatchingTerm }}</span>
      </li>

      <li class="autocomplete-option-attribution">
        <img src="../../images/powered_by_google_on_white_hdpi.png">
      </li>
    </ul>
  </div>
</template>

<script>
import debounce from 'lodash/debounce'
import { inject, nextTick } from 'vue'
import MapsService from '@/api/maps-service'

export default {
  // Avoid automatically inheriting the parent's attributes so they're only set explicitly
  // to the autocomplete input via v-bind.
  inheritAttrs: false,

  props: {
    allowEnterManuallyOption: {
      type: Boolean,
      required: false,
      default: false
    },

    inputPlaceholder: {
      type: String,
      required: false,
      default() {
        return inject('i18n').t('components.custom_gmap_autocomplete.enter_a_location')
      }
    },

    options: {
      type: Object,
      required: false,
      default: () => ({})
    },

    selectFirstOnEnter: {
      type: Boolean,
      default: true
    },

    value: {
      type: String,
      required: false
    }
  },

  emits: ['blur', 'enter-manually-selected', 'focus', 'input', 'place-changed'],

  data() {
    return {
      autocompleteOptions: [],
      autofocusing: false,
      query: this.value,
      focusedOption: null
    }
  },

  computed: {
    displayOptions() {
      return this.autocompleteOptions.length > 0
    },

    enterManuallyMainTerm() {
      return this.i18n.t('components.custom_gmap_autocomplete.enter_manually_option_main_text', { user_query: this.query })
    }
  },

  methods: {
    clearAutocompleteOptions() {
      this.focusedOption = null
      this.autocompleteOptions = []
    },

    clearQuery() {
      this.query = null
    },

    focus() {
      this.autofocusing = true
      this.$refs.autocomplete.focus()

      nextTick(() => {
        this.autofocusing = false
      })
    },

    focusNextOption() {
      if (this.autocompleteOptions.length === 0) return

      if (!this.focusedOption) {
        this.focusedOption = this.autocompleteOptions[0]
        return
      }

      const focusedOptionIndex = this.autocompleteOptions.indexOf(this.focusedOption)
      if (focusedOptionIndex === this.autocompleteOptions.length - 1) {
        this.focusedOption = null
      } else {
        this.focusedOption = this.autocompleteOptions[focusedOptionIndex + 1]
      }
    },

    focusPreviousOption() {
      if (this.autocompleteOptions.length === 0) return

      if (!this.focusedOption) {
        this.focusedOption = this.autocompleteOptions[this.autocompleteOptions.length - 1]
        return
      }

      const focusedOptionIndex = this.autocompleteOptions.indexOf(this.focusedOption)
      if (focusedOptionIndex === this.autocompleteOptions[0]) {
        this.focusedOption = null
      } else {
        this.focusedOption = this.autocompleteOptions[focusedOptionIndex - 1]
      }
    },

    lookUpPlaces() {
      if (this.query.length === 0) {
        // Input is empty. Clear the suggestion list and don't bother making an API call.
        this.clearAutocompleteOptions()
      } else {
        // Make an API call to get suggestions
        const getPlacePredictionOptions = {
          input: this.query,
          ...this.options
        }

        const predictionsForQuery = this.query
        MapsService.autocomplete(getPlacePredictionOptions).then((predictions) => {
          // Ignore the predictions response if the query that was used is different from the current. This can happen
          // when the API response comes in a different order than the requests, most likely during scenarios run when
          // there are multiple requests made in rapid succession.
          if (this.query !== predictionsForQuery) {
            return
          }

          this.autocompleteOptions = predictions.map((prediction) => {
            return {
              description: prediction.description,
              mainMatchingTerm: prediction.terms[0].value,
              secondaryMatchingTerm: this.secondaryMatchingTermsForDisplay(prediction.terms),
              placeId: prediction.place_id
            }
          })

          if (this.allowEnterManuallyOption) {
            this.autocompleteOptions.push({
              isManualInput: true,
              mainMatchingTerm: this.enterManuallyMainTerm,
              secondaryMatchingTerm: this.i18n.t('components.custom_gmap_autocomplete.enter_manually_option_subtitle')
            })
          }
        })
      }
    },

    onBlur(payload) {
      // The `blur` event is sent before the `click` event on the option. So if we removed from the DOM the
      // autocomplete option element, the click would never be sent. At the same time, the input can lose focus
      // in different ways, but the only one that we care is the click on one of the autocomplete options.
      // For this reason, we insert a small delay before closing the autocomplete options, allowing the
      // `click` event to be send.
      setTimeout(() => {
        this.clearAutocompleteOptions()
      }, 500)

      this.$emit('blur', payload)
    },

    onFocus(payload) {
      if (!this.autofocusing) {
        if (this.query) {
          this.lookUpPlaces()
        }

        this.$emit('focus', payload)
      }
    },

    onInput(e) {
      this.query = e.target.value
      this.$emit('input', this.query)

      this.debouncedLookUpPlaces()
    },

    debouncedLookUpPlaces: debounce(function() { this.lookUpPlaces() }, 200),

    onKeyDown(e) {
      const SPECIAL_KEYS = ['ArrowDown', 'ArrowUp', 'Enter']

      if (!SPECIAL_KEYS.includes(e.code)) return

      switch (e.code) {
      case 'ArrowDown':
        e.preventDefault()
        this.focusNextOption()
        break

      case 'ArrowUp':
        e.preventDefault()
        this.focusPreviousOption()
        break

      case 'Enter':
        if (this.focusedOption) {
          e.preventDefault()
          this.selectedOption(this.focusedOption)
        } else if (this.selectFirstOnEnter && this.autocompleteOptions.length > 0) {
          e.preventDefault()
          this.selectedOption(this.autocompleteOptions[0])
        }
        break
      }
    },

    optionIsFocused(autocompleteOption) {
      return this.focusedOption && autocompleteOption.placeId === this.focusedOption.placeId
    },

    secondaryMatchingTermsForDisplay(terms) {
      return terms.slice(1).map((term) => term.value ).join(', ')
    },

    selectedEnterManuallyOption() {
      this.$emit('enter-manually-selected', this.query)
    },

    selectedOption(option) {
      if (option.isManualInput) {
        this.selectedEnterManuallyOption()
        return
      }

      MapsService.getPlaceDetails({ placeId: option.placeId }).then((result) => {
        this.$emit('place-changed', result)
      })

      this.query = option.description
      this.clearAutocompleteOptions()
    }
  }
}
</script>

<style lang="scss" scoped>
@use "~friendship_styles/colors" as colors;

  .autocomplete-wrapper {
    position: relative;
  }

  .autocomplete-options {
    list-style: none;
    border: solid 1px colors.$separator-gray;
    border-radius: 6px;
    box-shadow: 0 2px 7px 0 rgba(0, 0, 0, 0.15);
    padding: 0;

    position: absolute;
    z-index: 1000;
    background-color: white;
    width: 100%;
  }

  .autocomplete-option {
    border-bottom: solid 1px colors.$separator-gray;
    padding: {
      left: 20px;
      top: 14px;
      bottom: 16px;
    }

    &:hover, &.selected {
      background-color: #f7f7f7;
    }

    .main-matching-term, .secondary-matching-terms {
      display: block;
    }

    .main-matching-term {
      font-size: 16px;
      font-weight: normal;
    }

    .secondary-matching-terms {
      font-size: 14px;
      color: #666666;
    }
  }

  .autocomplete-option-attribution {
    text-align: right;
    padding: 10px 20px 10px 20px;

    img {
      height: 18px;
    }
  }
</style>
